import React, { useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
  Textarea,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import { FormErrorDisplay, RootErrorDisplay } from '@/components/ui/FormErrorDisplay';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { 
  useCreateConditionGroupLookupMutation,
  useUpdateConditionGroupLookupMutation,
  useGetConditionGroupLookupQuery,
} from '../api';
import { useFormErrorHandler } from '@hooks/useFormErrorHandler';
import Select from '@/components/ui/Select';
import { LOOKUP_STATUSES, LOOKUP_STATUS_OPTIONS } from '@/utils/lookupStatus';

// Schema definition
const conditionGroupSchema = yup.object({
  name: yup
    .string()
    .required('Group name is required')
    .min(2, 'Name must be at least 2 characters long')
    .max(100, 'Name must be less than 100 characters'),
  description: yup
    .string()
    .nullable()
    .optional()
    .max(300, 'Description must be less than 300 characters'),
  status: yup
    .string()
    .required('Status is required')
    .oneOf(LOOKUP_STATUSES, 'Invalid status'),
  order: yup
    .number()
    .nullable()
    .optional()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null;
      }
      return value;
    })
    .test('order-validation', 'Order must be at least 1 and a whole number', function(value) {
      if (value === null || value === undefined) {
        return true; // Allow null/undefined
      }
      return Number.isInteger(value) && value >= 1;
    })
});

type ConditionGroupFormData = yup.InferType<typeof conditionGroupSchema>;

interface ConditionGroupCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of condition group to fetch (for hash modal edit mode)
  onSuccess?: () => void;
}

const ConditionGroupCreateEditModal: React.FC<ConditionGroupCreateEditModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { 
    data: fetchedGroup, 
    isLoading: isFetchingGroup, 
    error: fetchError 
  } = useGetConditionGroupLookupQuery(entityId!, {
    skip: !entityId // Only fetch when we have an entityId
  });

  // Use fetched data if available
  const editingGroup = fetchedGroup;
  const {
    handleSubmit,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<ConditionGroupFormData>({
    resolver: yupResolver(conditionGroupSchema) as any,
    defaultValues: {
      name: '',
      description: '',
      status: 'draft',
      order: null
    }
  });
  
  const { withErrorHandling, clearAllErrors } = useFormErrorHandler(setError, clearErrors);
  
  const [createGroup, { isLoading: isCreating }] = useCreateConditionGroupLookupMutation();
  const [updateGroup, { isLoading: isUpdating }] = useUpdateConditionGroupLookupMutation();
  
  const isLoading = isCreating || isUpdating || isSubmitting || isFetchingGroup;

  // Reset form when modal opens or editingGroup changes
  useEffect(() => {
    if (isOpen) {
      if (editingGroup) {
        reset({ 
          name: editingGroup.name, 
          description: editingGroup.description || '',
          status: editingGroup.status || 'draft', 
          order: editingGroup.order || null 
        });
      } else {
        reset({ name: '', description: '', status: 'draft', order: null });
      }
      // Clear any previous server errors
      clearAllErrors();
    }
  }, [isOpen, editingGroup, reset, clearAllErrors]);

  const onSubmit = async (data: ConditionGroupFormData) => {
    await withErrorHandling(async () => {
      const submitData = {
        name: data.name,
        description: data.description || null,
        status: data.status as any,
        order: data.order
      };
      
      if (editingGroup) {
        await updateGroup({
          id: editingGroup.id,
          data: submitData
        }).unwrap();
      } else {
        await createGroup(submitData).unwrap();
      }
      
      onSuccess?.();
      onClose();
    });
  };

  const handleClose = () => {
    reset({ name: '', description: '', status: 'draft', order: null });
    clearAllErrors();
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle
          color="brand.700" 
          _dark={{ color: "brand.300" }}
          fontSize="md"
          fontWeight="semibold"
        >
          {editingGroup ? 'Edit Group' : 'Create New Group'}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        {fetchError ? (
          <Text color="red.500" textAlign="center" py={4}>
            Error loading condition group data. Please try again.
          </Text>
        ) : isFetchingGroup ? (
          <Text textAlign="center" py={4}>Loading condition group data...</Text>
        ) : (
          <form onSubmit={handleSubmit(onSubmit as any)} id="group-form">
            <VStack gap={4} align="stretch">
              {/* Display root/general errors using component */}
              <RootErrorDisplay error={errors.root} />
            
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Group Name
              </Text>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter group name"
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.name ? "red.500" : undefined}
                  />
                )}
              />
              <FormErrorDisplay error={errors.name} />
            </Box>
            
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Description <Text as="span" color="gray.500" fontSize="xs">(optional)</Text>
              </Text>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value || ''}
                    placeholder="Enter group description (optional)"
                    rows={3}
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.description ? "red.500" : undefined}
                    resize="vertical"
                  />
                )}
              />
              <FormErrorDisplay error={errors.description} />
            </Box>
            
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Status
              </Text>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    options={LOOKUP_STATUS_OPTIONS}
                  />
                )}
              />
              <FormErrorDisplay error={errors.status} />
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Display Order <Text as="span" color="gray.500" fontSize="xs">(optional)</Text>
              </Text>
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    placeholder="Enter display order (optional)"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? null : parseInt(value) || null);
                    }}
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.order ? "red.500" : undefined}
                  />
                )}
              />
              <FormErrorDisplay error={errors.order} />
            </Box>
          </VStack>
        </form>
        )}
      </DrawerBody>
      
      <DrawerFooter>
        <HStack justify="end" gap={3}>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {!fetchError && !isFetchingGroup && (
            <Button
              type="submit"
              form="group-form"
              colorPalette="brand"
              loading={isLoading}
              disabled={isLoading}
            >
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          )}
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const ConditionGroupCreateModal = withHashDrawer(ConditionGroupCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const ConditionGroupEditModal = withHashDrawer(ConditionGroupCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default ConditionGroupCreateModal; 